/**
 * Templates Zustand Store
 *
 * Manages all state for Global Templates including:
 * - Template list with filters and pagination
 * - Active template details
 * - Template CRUD operations
 * - Visibility management
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  GlobalTemplate,
  TemplateListFilters,
  PaginationMeta,
  CreateTemplateDto,
  UpdateTemplateDto,
  UpdateTemplateVisibilityDto,
  TemplateCategory,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface TemplatesError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface TemplatesState {
  // Templates List
  templates: GlobalTemplate[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: TemplatesError | null;

  // Filters
  filters: TemplateListFilters;

  // Active Template
  activeTemplateId: string | null;
  activeTemplate: GlobalTemplate | null;
  isLoadingTemplate: boolean;
  templateError: TemplatesError | null;

  // Action States
  isPerformingAction: boolean;
  actionError: TemplatesError | null;

  // Actions - List
  loadTemplates: (page?: number) => Promise<void>;
  refreshTemplates: () => Promise<void>;

  // Actions - Filters
  setFilter: <K extends keyof TemplateListFilters>(key: K, value: TemplateListFilters[K]) => void;
  clearFilters: () => void;

  // Actions - Active Template
  loadTemplate: (id: string) => Promise<void>;
  selectTemplate: (id: string) => void;
  clearActiveTemplate: () => void;

  // Actions - CRUD
  createTemplate: (dto: CreateTemplateDto) => Promise<string | null>;
  updateTemplate: (id: string, dto: UpdateTemplateDto) => Promise<boolean>;
  updateVisibility: (id: string, dto: UpdateTemplateVisibilityDto) => Promise<boolean>;
  duplicateTemplate: (id: string) => Promise<string | null>;
  deleteTemplate: (id: string) => Promise<boolean>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Templates List
  templates: [] as GlobalTemplate[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as TemplatesError | null,

  // Filters
  filters: {} as TemplateListFilters,

  // Active Template
  activeTemplateId: null as string | null,
  activeTemplate: null as GlobalTemplate | null,
  isLoadingTemplate: false,
  templateError: null as TemplatesError | null,

  // Action States
  isPerformingAction: false,
  actionError: null as TemplatesError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useTemplatesStore = create<TemplatesState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadTemplates: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      // Apply filters
      if (filters.search) params.set('search', filters.search);
      if (filters.category) params.set('category', filters.category);
      if (filters.visibility) params.set('visibility', filters.visibility);
      if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));

      const response = await api.get<{
        success: boolean;
        data: { data: GlobalTemplate[]; pagination: PaginationMeta };
      }>(`${API_ENDPOINTS.SUPER_ADMIN.TEMPLATES.LIST}?${params.toString()}`);

      set({
        templates: response.data.data,
        pagination: response.data.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_TEMPLATES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load templates',
        },
      });
    }
  },

  refreshTemplates: async () => {
    const { pagination } = get();
    await get().loadTemplates(pagination?.page || 1);
  },

  // ============================================
  // Filter Actions
  // ============================================

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadTemplates(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadTemplates(1);
  },

  // ============================================
  // Active Template Actions
  // ============================================

  loadTemplate: async (id: string) => {
    set({ activeTemplateId: id, isLoadingTemplate: true, templateError: null });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: GlobalTemplate;
      }>(API_ENDPOINTS.SUPER_ADMIN.TEMPLATES.TEMPLATE(id));

      set({ activeTemplate: response.data, isLoadingTemplate: false });
    } catch (error) {
      set({
        isLoadingTemplate: false,
        templateError: {
          code: 'LOAD_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load template',
        },
      });
    }
  },

  selectTemplate: (id: string) => {
    const { templates } = get();
    const template = templates.find((t) => t.id === id);
    if (template) {
      set({ activeTemplateId: id, activeTemplate: template });
    } else {
      get().loadTemplate(id);
    }
  },

  clearActiveTemplate: () => {
    set({
      activeTemplateId: null,
      activeTemplate: null,
      templateError: null,
    });
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createTemplate: async (dto: CreateTemplateDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<{
        success: boolean;
        data: GlobalTemplate;
      }>(API_ENDPOINTS.SUPER_ADMIN.TEMPLATES.CREATE, dto);

      // Refresh the list
      await get().refreshTemplates();
      set({ isPerformingAction: false });
      return response.data.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create template',
        },
      });
      return null;
    }
  },

  updateTemplate: async (id: string, dto: UpdateTemplateDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.put<{
        success: boolean;
        data: GlobalTemplate;
      }>(API_ENDPOINTS.SUPER_ADMIN.TEMPLATES.UPDATE(id), dto);

      // Update local state
      set((state) => ({
        templates: state.templates.map((t) =>
          t.id === id ? { ...t, ...response.data } : t
        ),
        activeTemplate:
          state.activeTemplate?.id === id
            ? { ...state.activeTemplate, ...response.data }
            : state.activeTemplate,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update template',
        },
      });
      return false;
    }
  },

  updateVisibility: async (id: string, dto: UpdateTemplateVisibilityDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.put<{
        success: boolean;
        data: GlobalTemplate;
      }>(API_ENDPOINTS.SUPER_ADMIN.TEMPLATES.VISIBILITY(id), dto);

      // Update local state
      set((state) => ({
        templates: state.templates.map((t) =>
          t.id === id ? { ...t, ...response.data } : t
        ),
        activeTemplate:
          state.activeTemplate?.id === id
            ? { ...state.activeTemplate, ...response.data }
            : state.activeTemplate,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_VISIBILITY_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update visibility',
        },
      });
      return false;
    }
  },

  duplicateTemplate: async (id: string): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<{
        success: boolean;
        data: GlobalTemplate;
      }>(API_ENDPOINTS.SUPER_ADMIN.TEMPLATES.DUPLICATE(id), {});

      // Refresh the list
      await get().refreshTemplates();
      set({ isPerformingAction: false });
      return response.data.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DUPLICATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to duplicate template',
        },
      });
      return null;
    }
  },

  deleteTemplate: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.TEMPLATES.DELETE(id));

      // Remove from local state
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        activeTemplate: state.activeTemplate?.id === id ? null : state.activeTemplate,
        activeTemplateId: state.activeTemplateId === id ? null : state.activeTemplateId,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete template',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({ listError: null, templateError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get templates by category */
export const useTemplatesByCategory = (category: TemplateCategory) => {
  return useTemplatesStore((state) =>
    state.templates.filter((t) => t.category === category)
  );
};

/** Get public templates count */
export const usePublicTemplatesCount = () => {
  return useTemplatesStore((state) =>
    state.templates.filter((t) => t.visibility === 'all').length
  );
};

/** Get active templates count */
export const useActiveTemplatesCount = () => {
  return useTemplatesStore((state) =>
    state.templates.filter((t) => t.isActive).length
  );
};

/** Check if any loading state is active */
export const useTemplatesLoading = () => {
  const isLoadingList = useTemplatesStore((state) => state.isLoadingList);
  const isLoadingTemplate = useTemplatesStore((state) => state.isLoadingTemplate);
  const isPerformingAction = useTemplatesStore((state) => state.isPerformingAction);
  return isLoadingList || isLoadingTemplate || isPerformingAction;
};

/** Get template by ID */
export const useTemplateById = (id: string) => {
  return useTemplatesStore((state) =>
    state.templates.find((t) => t.id === id)
  );
};

/** Get active filters count */
export const useActiveFiltersCount = () => {
  return useTemplatesStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.category) count++;
    if (state.filters.visibility) count++;
    if (state.filters.isActive !== undefined) count++;
    return count;
  });
};
